import { dayjs } from '@/extendedDayjs';
import {
    AppPlatform,
    FixRequestStatusType,
    IntegrationType,
    type IssueBuild,
    type IssueEntity,
    type IssueSource,
    IssueSourceType,
    IssueState,
    PotentialCause,
    TicketStatusCategory,
    type TimelineEvent,
    TimelineEventInitiator,
    TimelineEventStatus,
    TimelineEventType,
} from '@/types';
import type { StringKeyedDictionary } from '@/typescriptHelpers';
import type { IssuesRequest, IssuesResponse, TriageSortBy } from '@/actions/triage';
import { getIsApplePlatform, getIsOTAPlatform, isAndroidPlatform } from '@/helpers';
import { mockOrgUsers } from '@/testData/orgUsers';

export interface IssuesDemoDataForApp {
    issues: IssueEntity[];
    timelinesByIssueId: StringKeyedDictionary<TimelineEvent[]>;
}

const demoUserId = (firstName: string): string => {
    const user = mockOrgUsers.find((orgUser) => orgUser.firstName === firstName);
    if (!user) {
        throw new Error(`No demo org user named ${firstName} — keep these in sync with mockOrgUsers.`);
    }
    return user.id;
};

const DEMO_USERS = {
    sully: demoUserId('Sully'),
    amelia: demoUserId('Amelia'),
    neil: demoUserId('Neil'),
    mae: demoUserId('Mae'),
};

const DEMO_REPO_URL = 'https://github.com/demo/DEMO-ios';
const DEMO_JIRA_BROWSE_URL = 'https://demo.atlassian.net/browse';
const DEMO_TESTRAIL_URL = 'https://demo.testrail.io';

const hoursAgo = (hours: number) => dayjs().subtract(hours, 'hours').toISOString();

interface IssueSeed {
    slug: string;
    title: string;
    description: string;
    state: IssueState;
    /** Index into the app's versions, most recent first. Clamped to what the app actually has. */
    versionIndex: number;
    build?: string;
    detectedHoursAgo: number;
    updatedHoursAgo: number;
    sources: (context: SeedContext) => IssueSource[];
    reporter?: string;
    assigneeUserIds?: string[];
    isAutoAssigned?: boolean;
    assigneeGroupIds?: string[];
    potentialCauses?: IssueEntity['potentialCauses'];
    fixRequest?: (context: SeedContext) => IssueEntity['fixRequest'];
    /** Slugs of other seeds in the same app. */
    relatedSlugs?: string[];
    ticket?: { key: string; url: string };
}

interface SeedContext {
    version: string;
    build: IssueBuild;
    crashReporter: IntegrationType;
    analytics: IntegrationType;
    storeName: string;
}

const ISSUE_SEEDS: IssueSeed[] = [
    {
        slug: 'delete-account-crash',
        title: 'Crash when confirming account deletion from the profile page',
        description:
            'Fatal null dereference on the delete account confirmation sheet when the profile was loaded from cache. Spiking since the rollout started.',
        state: IssueState.New,
        versionIndex: 0,
        detectedHoursAgo: 6,
        updatedHoursAgo: 6,
        sources: ({ version, build, crashReporter }) => [
            {
                id: 'source-stability',
                type: IssueSourceType.StabMon,
                integrationType: crashReporter,
                createdAt: hoursAgo(6),
                healthMetrics: {
                    metricId: 'crash_free_sessions',
                    metricName: 'Session stability',
                    integrationType: crashReporter,
                    currentValue: 98.1,
                    configuredThreshold: { value: 99.5, condition: 'below' },
                    build: build.identifier,
                    version,
                },
            },
        ],
        potentialCauses: {
            aiTitle: 'Most likely caused by the delete account CTA',
            aiDescription:
                'The crash signature appears only on builds that contain the delete account CTA, and the affected code path is covered by a feature flag that was ramped to 50% the same day.',
            causes: [
                {
                    cause: PotentialCause.Code,
                    citations: [
                        {
                            type: 'pullRequest',
                            label: '#121 Add support for delete account',
                            url: `${DEMO_REPO_URL}/pull/121`,
                            referenceId: '121',
                        },
                        {
                            type: 'commit',
                            label: 'a91f4c2 Add CTA in profile page for deleting account',
                            url: `${DEMO_REPO_URL}/commit/a91f4c2b1d8e4c0f9a7d3e2b5c6f1a8d4e7b0c93`,
                        },
                    ],
                },
                {
                    cause: PotentialCause.FeatureFlag,
                    citations: [{ type: 'featureFlag', label: 'delete_account_cta', referenceId: 'ff-delete-account' }],
                },
            ],
        },
    },
    {
        slug: 'slow-startup',
        title: 'Users report slow app startup after the latest update',
        description:
            'Reviews mention 5-10s cold starts since the last release, corroborated by the cold start metric crossing its threshold.',
        state: IssueState.New,
        versionIndex: 0,
        detectedHoursAgo: 20,
        updatedHoursAgo: 20,
        relatedSlugs: ['delete-account-crash'],
        sources: ({ version, build, analytics, storeName }) => [
            {
                id: 'source-reviews',
                type: IssueSourceType.UserReviews,
                createdAt: hoursAgo(20),
                displayText: `${storeName} reviews`,
                userReviews: {
                    aiDescription: 'Multiple 1-2 star reviews describing very slow cold starts since the update.',
                    version,
                    sampleReviews: [
                        {
                            author: 'jenna_k',
                            rating: '1',
                            title: 'Unusably slow now',
                            content: 'Takes about ten seconds to open since the update. It used to be instant.',
                            submittedAt: hoursAgo(22),
                        },
                        {
                            author: 'marcusp',
                            rating: '2',
                            title: 'Slow launch',
                            content: 'Love the app but the launch time got much worse in this version.',
                            submittedAt: hoursAgo(30),
                        },
                    ],
                },
            },
            {
                id: 'source-obaa',
                type: IssueSourceType.OBAA,
                integrationType: analytics,
                createdAt: hoursAgo(19),
                healthMetrics: {
                    metricId: 'cold_start_time',
                    metricName: 'cold_start_time',
                    integrationType: analytics,
                    currentValue: 8.2,
                    configuredThreshold: { value: 3, condition: 'above' },
                    build: build.identifier,
                    version,
                },
            },
        ],
        potentialCauses: {
            aiTitle: 'Cold start regression traced to a new SDK on the startup path',
            aiDescription:
                'Cold start time regressed on the same build that added an analytics SDK initialized synchronously on the main thread.',
            causes: [
                {
                    cause: PotentialCause.Dependency,
                    citations: [
                        {
                            type: 'commit',
                            label: 'c30ab19 Configure sentry',
                            url: `${DEMO_REPO_URL}/commit/c30ab1947f2e8d6b0c5a3419e7d2b8f60a4c1d5e`,
                        },
                    ],
                },
            ],
        },
    },
    {
        slug: 'login-unresponsive',
        title: 'Beta testers: login button unresponsive on first tap',
        description:
            'Several testers report the login button does nothing on the first tap and only works after backgrounding the app.',
        state: IssueState.Assigned,
        isAutoAssigned: true,
        versionIndex: 1,
        detectedHoursAgo: 44,
        updatedHoursAgo: 30,
        assigneeUserIds: [DEMO_USERS.neil],
        sources: ({ version, build }) => [
            {
                id: 'source-beta',
                type: IssueSourceType.BetaFeedback,
                createdAt: hoursAgo(44),
                betaFeedback: {
                    aiDescription: 'Unresponsive login button reported by multiple beta testers on the same build.',
                    version,
                    build: build.identifier,
                    sampleFeedback: [
                        {
                            comment:
                                'Tapping "Log in" does nothing the first time. Works after I switch apps and come back.',
                            testerName: 'Mae Jemison',
                            testerEmail: DEMO_USERS.mae,
                            build: build.identifier,
                            submittedAt: hoursAgo(44),
                        },
                        {
                            comment: 'Same here, first tap on login is swallowed.',
                            testerName: 'Amelia Earhart',
                            testerEmail: DEMO_USERS.amelia,
                            build: build.identifier,
                            submittedAt: hoursAgo(41),
                        },
                    ],
                },
            },
        ],
    },
    {
        slug: 'favorites-empty',
        title: 'Favorites list renders empty after favoriting from the homepage',
        description:
            'Items favorited from the homepage never show up in the favorites list. Reported by support and reproduced once on the latest beta build.',
        state: IssueState.Assigned,
        versionIndex: 1,
        detectedHoursAgo: 60,
        updatedHoursAgo: 36,
        reporter: DEMO_USERS.amelia,
        assigneeUserIds: [DEMO_USERS.sully, DEMO_USERS.mae],
        sources: ({ version, build }) => [
            {
                id: 'source-runway',
                type: IssueSourceType.Runway,
                createdAt: hoursAgo(60),
                runway: {
                    reporter: DEMO_USERS.amelia,
                    title: 'Favorites list renders empty',
                    description:
                        'Two support tickets this morning with screenshots of an empty favorites list after tapping the star on the homepage. Both users are on the latest build.',
                    version,
                    build: build.identifier,
                },
            },
        ],
    },
    {
        slug: 'push-permissions',
        title: 'Push notification permission prompt never appears on first launch',
        description:
            'The permission prompt is skipped on a clean install, so notifications stay off until the user digs into settings.',
        state: IssueState.FixRequested,
        versionIndex: 0,
        detectedHoursAgo: 80,
        updatedHoursAgo: 12,
        reporter: DEMO_USERS.neil,
        assigneeUserIds: [DEMO_USERS.sully],
        isAutoAssigned: true,
        sources: ({ version, build }) => [
            {
                id: 'source-runway',
                type: IssueSourceType.Runway,
                createdAt: hoursAgo(80),
                runway: {
                    reporter: DEMO_USERS.neil,
                    description: 'Clean install on a fresh device never shows the notification prompt.',
                    version,
                    build: build.identifier,
                },
            },
        ],
        fixRequest: ({ version }) => ({
            id: 'fix-request-push',
            createdBy: DEMO_USERS.neil,
            createdAt: hoursAgo(12),
            formFieldsSettings: [],
            formFieldsValues: [
                {
                    fieldId: 'summary',
                    value: 'Request the notification permission after onboarding completes instead of during it',
                },
            ],
            status: { status: FixRequestStatusType.FixRequestPending, updatedAt: hoursAgo(12), authorIds: [] },
            fixVersion: version,
        }),
    },
    {
        slug: 'search-results-empty',
        title: 'Search returns no results for accented queries',
        description:
            'Queries containing accented characters return an empty result set. Merged from reviews, a support ticket and the regression suite.',
        state: IssueState.FixApproved,
        versionIndex: 1,
        detectedHoursAgo: 120,
        updatedHoursAgo: 18,
        assigneeUserIds: [DEMO_USERS.mae],
        assigneeGroupIds: ['engineer'],
        ticket: { key: 'DEMO-51', url: `${DEMO_JIRA_BROWSE_URL}/DEMO-51` },
        sources: ({ version, build, storeName }) => [
            {
                id: 'source-reviews',
                type: IssueSourceType.UserReviews,
                createdAt: hoursAgo(120),
                displayText: `${storeName} reviews`,
                userReviews: {
                    aiDescription: 'Reviews from non-English locales reporting that search finds nothing.',
                    version,
                    sampleReviews: [
                        {
                            author: 'lucie.m',
                            rating: '2',
                            title: 'La recherche ne marche pas',
                            content: 'Impossible de trouver un produit dont le nom contient un accent.',
                            submittedAt: hoursAgo(121),
                        },
                    ],
                },
            },
            {
                id: 'source-ticket',
                type: IssueSourceType.PMTicket,
                integrationType: IntegrationType.Jira,
                createdAt: hoursAgo(110),
                pmTicket: {
                    integrationType: IntegrationType.Jira,
                    ticketId: 'DEMO-51',
                    ticketTitle: 'Search drops accented characters',
                    ticketDescription: 'Normalize the query before hitting the search index.',
                    ticketUrl: `${DEMO_JIRA_BROWSE_URL}/DEMO-51`,
                    projectName: 'Demo',
                    projectUrl: `${DEMO_JIRA_BROWSE_URL}/DEMO`,
                    reporter: DEMO_USERS.amelia,
                    status: 'In Progress',
                    statusCategory: TicketStatusCategory.InProgress,
                    assignee: 'Mae Jemison',
                    assigneeEmail: DEMO_USERS.mae,
                    fixVersion: version,
                    labels: ['search', 'i18n'],
                },
            },
            {
                id: 'source-regression',
                type: IssueSourceType.RegressionTesting,
                integrationType: IntegrationType.TestRail,
                createdAt: hoursAgo(108),
                regressionTesting: {
                    integrationType: IntegrationType.TestRail,
                    testRunName: 'Regression - Search',
                    testRunUrl: `${DEMO_TESTRAIL_URL}/index.php?/runs/view/1204`,
                    failedTestCaseName: 'Search with diacritics returns matches',
                    failedTestCaseId: 'C4102',
                    version,
                    build: build.identifier,
                },
            },
        ],
        fixRequest: ({ version }) => ({
            id: 'fix-request-search',
            createdBy: DEMO_USERS.amelia,
            createdAt: hoursAgo(40),
            formFieldsSettings: [],
            formFieldsValues: [
                { fieldId: 'summary', value: 'Normalize search queries before querying the index' },
                { fieldId: 'impact', value: 'Search is effectively broken for every non-English locale.' },
            ],
            status: {
                status: FixRequestStatusType.FixRequestApproved,
                updatedAt: hoursAgo(18),
                authorId: DEMO_USERS.sully,
                authorIds: [DEMO_USERS.sully],
            },
            fixVersion: version,
            hasCodeChangesPendingOrMerged: true,
        }),
    },
    {
        slug: 'offline-mode-regression',
        title: 'Offline mode regression resurfaced after the last rollout',
        description:
            'Cached content is dropped when the device comes back online. Previously fixed, reopened after new reports on the current version.',
        state: IssueState.Reopened,
        versionIndex: 0,
        detectedHoursAgo: 200,
        updatedHoursAgo: 8,
        assigneeUserIds: [DEMO_USERS.neil],
        sources: ({ version, build }) => [
            {
                id: 'source-regression',
                type: IssueSourceType.RegressionTesting,
                integrationType: IntegrationType.TestRail,
                createdAt: hoursAgo(200),
                regressionTesting: {
                    integrationType: IntegrationType.TestRail,
                    testRunName: 'Regression - Offline',
                    testRunUrl: `${DEMO_TESTRAIL_URL}/index.php?/runs/view/1188`,
                    failedTestCaseName: 'Cached feed survives reconnect',
                    failedTestCaseId: 'C3987',
                    version,
                    build: build.identifier,
                },
            },
        ],
    },
    {
        slug: 'deeplink-onboarding',
        title: 'Deep links open onboarding instead of the target screen',
        description:
            'Marketing deep links landed users on the onboarding carousel. Fixed and merged into the current release.',
        state: IssueState.FixMerged,
        versionIndex: 1,
        detectedHoursAgo: 260,
        updatedHoursAgo: 50,
        assigneeUserIds: [DEMO_USERS.sully],
        ticket: { key: 'DEMO-48', url: `${DEMO_JIRA_BROWSE_URL}/DEMO-48` },
        sources: ({ version, build }) => [
            {
                id: 'source-runway',
                type: IssueSourceType.Runway,
                createdAt: hoursAgo(260),
                runway: {
                    reporter: DEMO_USERS.mae,
                    description: 'Campaign links all resolve to onboarding on a cold start.',
                    version,
                    build: build.identifier,
                },
            },
        ],
        fixRequest: ({ version }) => ({
            id: 'fix-request-deeplink',
            createdBy: DEMO_USERS.mae,
            createdAt: hoursAgo(180),
            formFieldsSettings: [],
            formFieldsValues: [{ fieldId: 'summary', value: 'Resolve the deep link before presenting onboarding' }],
            status: {
                status: FixRequestStatusType.FixRequestApproved,
                updatedAt: hoursAgo(170),
                authorId: DEMO_USERS.sully,
                authorIds: [DEMO_USERS.sully],
            },
            fixVersion: version,
            hasCodeChangesPendingOrMerged: true,
        }),
    },
    {
        slug: 'dark-mode-contrast',
        title: 'Low contrast on secondary labels in dark mode',
        description:
            'Secondary labels are hard to read in dark mode. Fix request rejected in favour of the design system refresh.',
        state: IssueState.FixRejected,
        versionIndex: 1,
        detectedHoursAgo: 300,
        updatedHoursAgo: 96,
        assigneeUserIds: [DEMO_USERS.amelia],
        sources: ({ version, storeName }) => [
            {
                id: 'source-reviews',
                type: IssueSourceType.UserReviews,
                createdAt: hoursAgo(300),
                displayText: `${storeName} reviews`,
                userReviews: {
                    aiDescription: 'A handful of reviews mentioning unreadable grey text in dark mode.',
                    version,
                },
            },
        ],
        fixRequest: ({ version }) => ({
            id: 'fix-request-contrast',
            createdBy: DEMO_USERS.amelia,
            createdAt: hoursAgo(140),
            formFieldsSettings: [],
            formFieldsValues: [{ fieldId: 'summary', value: 'Bump secondary label contrast in dark mode' }],
            status: {
                status: FixRequestStatusType.FixRequestRejected,
                updatedAt: hoursAgo(96),
                authorId: DEMO_USERS.sully,
                authorIds: [DEMO_USERS.sully],
            },
            fixVersion: version,
        }),
    },
    {
        slug: 'settings-copy',
        title: 'Outdated copy in the notification settings screen',
        description:
            'Copy still references the old notification categories. Tracked with the design refresh, not a priority for this release.',
        state: IssueState.NotAPriority,
        versionIndex: 1,
        detectedHoursAgo: 340,
        updatedHoursAgo: 120,
        ticket: { key: 'DEMO-46', url: `${DEMO_JIRA_BROWSE_URL}/DEMO-46` },
        sources: () => [
            {
                id: 'source-ticket',
                type: IssueSourceType.PMTicket,
                integrationType: IntegrationType.Jira,
                createdAt: hoursAgo(340),
                pmTicket: {
                    integrationType: IntegrationType.Jira,
                    ticketId: 'DEMO-46',
                    ticketTitle: 'Refresh notification settings copy',
                    ticketDescription: 'Align the copy with the new notification categories.',
                    ticketUrl: `${DEMO_JIRA_BROWSE_URL}/DEMO-46`,
                    projectName: 'Demo',
                    projectUrl: `${DEMO_JIRA_BROWSE_URL}/DEMO`,
                    reporter: DEMO_USERS.mae,
                    status: 'Backlog',
                    statusCategory: TicketStatusCategory.Todo,
                    labels: ['copy'],
                },
            },
        ],
    },
];

const STATE_TRANSITION_FROM: Partial<Record<IssueState, IssueState>> = {
    [IssueState.Assigned]: IssueState.New,
    [IssueState.FixRequested]: IssueState.Assigned,
    [IssueState.FixApproved]: IssueState.FixRequested,
    [IssueState.FixMerged]: IssueState.FixApproved,
    [IssueState.FixRejected]: IssueState.FixRequested,
    [IssueState.NotAPriority]: IssueState.New,
    [IssueState.Reopened]: IssueState.FixMerged,
};

// CI build numbers and their App Store counterparts follow the same convention as the other demo
// fixtures (testData/appHome.ts): iOS pairs CI `275` with App Store `27500`, Android reuses one number.
const buildForIndex = (platform: AppPlatform, index: number): IssueBuild => {
    const isAndroid = isAndroidPlatform(platform);
    const ciIdentifier = `${(isAndroid ? 56 : 275) + index}`;

    if (getIsOTAPlatform(platform)) {
        return { identifier: ciIdentifier, integrationId: IntegrationType.Bitrise };
    }

    return {
        identifier: ciIdentifier,
        integrationId: IntegrationType.Bitrise,
        appStoreBuild: isAndroid ? ciIdentifier : `${ciIdentifier}00`,
        appStoreIntegrationId: isAndroid ? IntegrationType.Google : IntegrationType.Apple,
        appStoreBuildIsProcessed: true,
    };
};

const timelineEventBase = (appId: string, triggeredAt: string) => ({
    appId,
    orgId: 'demo',
    status: TimelineEventStatus.Completed,
    releaseStepArchetypes: [],
    destinationStepConfigIds: null,
    previouslySeenWithErrorAt: [],
    previouslySeenCount: 0,
    triggeredAt,
});

// `titleForEvent` has no case for triage events, so it falls back to the raw event type — the real
// titles are computed server-side and shipped on the event. Mirrors lib/common/timeline_events.go.
const SOURCE_DISPLAY_NAMES: Record<IssueSourceType, string> = {
    [IssueSourceType.Runway]: 'Team',
    [IssueSourceType.PMTicket]: 'PM ticket',
    [IssueSourceType.UserReviews]: 'User reviews',
    [IssueSourceType.BetaFeedback]: 'Beta feedback',
    [IssueSourceType.RegressionTesting]: 'Regression testing',
    [IssueSourceType.StabMon]: 'Stability monitoring',
    [IssueSourceType.OBAA]: 'Observability & analytics',
};

const timelineForIssue = (appId: string, issue: IssueEntity, seed: IssueSeed): TimelineEvent[] => {
    const primarySource = issue.sources[0];
    const events: TimelineEvent[] = issue.sources.map((source) => {
        const isPrimary = source.id === primarySource?.id;
        const sourceDisplayName = SOURCE_DISPLAY_NAMES[source.type];

        return {
            ...timelineEventBase(appId, source.createdAt ?? issue.createdAt),
            type: isPrimary ? TimelineEventType.TriageIssueDetected : TimelineEventType.TriageIssueSourceAdded,
            title:
                isPrimary ?
                    `Triage issue detected from ${sourceDisplayName}`
                :   `Triage issue source added: ${sourceDisplayName}`,
            triggeredBy:
                source.type === IssueSourceType.Runway ? TimelineEventInitiator.User : TimelineEventInitiator.Runway,
            userId: source.type === IssueSourceType.Runway ? source.runway?.reporter : undefined,
            eventData: {
                issueId: issue.id,
                sourceType: source.type,
                sourceDisplayName,
            },
        } as unknown as TimelineEvent;
    });

    if (issue.assigneeUserIds.length) {
        events.push({
            ...timelineEventBase(appId, hoursAgo(seed.detectedHoursAgo - 1)),
            type: TimelineEventType.TriageIssueAssigneeChanged,
            title: 'Triage issue assigned',
            triggeredBy: issue.isAutoAssigned ? TimelineEventInitiator.Runway : TimelineEventInitiator.User,
            userId: issue.isAutoAssigned ? undefined : DEMO_USERS.sully,
            eventData: { issueId: issue.id, previousAssignees: [], newAssignees: issue.assigneeUserIds },
        } as unknown as TimelineEvent);
    }

    const previousStatus = STATE_TRANSITION_FROM[issue.state];
    if (previousStatus) {
        events.push({
            ...timelineEventBase(appId, issue.updatedAt),
            type: TimelineEventType.TriageIssueStatusUpdated,
            title: `Triage issue status updated from "${previousStatus}" to "${issue.state}"`,
            triggeredBy: TimelineEventInitiator.User,
            userId: DEMO_USERS.sully,
            eventData: { issueId: issue.id, previousStatus, newStatus: issue.state },
        } as unknown as TimelineEvent);
    }

    if (seed.ticket) {
        events.push({
            ...timelineEventBase(appId, hoursAgo(seed.updatedHoursAgo + 2)),
            type: TimelineEventType.TriageIssueTicketCreated,
            title: `Ticket created: ${seed.ticket.key}`,
            triggeredBy: TimelineEventInitiator.Runway,
            eventData: { issueId: issue.id, ticketKey: seed.ticket.key, ticketUrl: seed.ticket.url },
        } as unknown as TimelineEvent);
    }

    return events.sort((a, b) => dayjs(b.triggeredAt).valueOf() - dayjs(a.triggeredAt).valueOf());
};

/**
 * Sources Runway can't actually produce for an app are dropped, so the demo doesn't show e.g. TestFlight
 * feedback on an Android-only app (beta feedback analysis is Apple-only, see RefreshBetaFeedbackML) or store
 * reviews on an OTA-only app. An issue whose every source is inapplicable is dropped entirely.
 */
const isSourceApplicable = (source: IssueSource, platforms: AppPlatform[]): boolean => {
    switch (source.type) {
        case IssueSourceType.BetaFeedback:
            return platforms.some(getIsApplePlatform);
        case IssueSourceType.UserReviews:
            return platforms.some((platform) => !getIsOTAPlatform(platform));
        default:
            return true;
    }
};

/**
 * Builds a coherent set of triage issues for one demo app. Versions are expected most-recent-first;
 * seeds index into them so every issue points at a version the demo app actually has. Platforms are the
 * app's destination platforms, which decide which sources are plausible.
 */
export const generateIssuesForApp = (args: {
    appId: string;
    platforms: AppPlatform[];
    versions: string[];
}): IssuesDemoDataForApp => {
    const { appId, platforms, versions } = args;

    if (!versions.length || !platforms.length) {
        return { issues: [], timelinesByIssueId: {} };
    }

    const primaryPlatform = platforms.find((platform) => !getIsOTAPlatform(platform)) ?? platforms[0];
    const isAndroid = isAndroidPlatform(primaryPlatform);
    const context = (versionIndex: number, seedIndex: number): SeedContext => ({
        version: versions[Math.min(versionIndex, versions.length - 1)],
        build: buildForIndex(primaryPlatform, seedIndex),
        crashReporter: IntegrationType.Sentry,
        analytics: isAndroid ? IntegrationType.AndroidVitals : IntegrationType.Firebase,
        storeName: isAndroid ? 'Google Play' : 'App Store',
    });

    const idForSlug = (slug: string) => `${appId}-issue-${slug}`;

    const seedsWithSources = ISSUE_SEEDS.map((seed, seedIndex) => {
        const seedContext = context(seed.versionIndex, seedIndex);
        return {
            seed,
            seedContext,
            sources: seed.sources(seedContext).filter((source) => isSourceApplicable(source, platforms)),
        };
    }).filter(({ sources }) => sources.length > 0);

    const keptSlugs = new Set(seedsWithSources.map(({ seed }) => seed.slug));

    const issues = seedsWithSources.map(({ seed, seedContext, sources }) => {
        return {
            id: idForSlug(seed.slug),
            appId,
            title: seed.title,
            description: seed.description,
            state: seed.state,
            versions: [seedContext.version],
            build: seedContext.build,
            sources,
            fixRequest: seed.fixRequest?.(seedContext),
            reporter: seed.reporter,
            assigneeUserIds: seed.assigneeUserIds ?? [],
            isAutoAssigned: seed.isAutoAssigned,
            suggestedAssigneeUserIds: [],
            assigneeGroupIds: seed.assigneeGroupIds ?? [],
            potentialCauses: seed.potentialCauses,
            relatedIssueIds: (seed.relatedSlugs ?? []).filter((slug) => keptSlugs.has(slug)).map(idForSlug),
            relatedResolutionItemIds: [],
            createdAt: hoursAgo(seed.detectedHoursAgo),
            updatedAt: hoursAgo(seed.updatedHoursAgo),
        } as IssueEntity;
    });

    const timelinesByIssueId: StringKeyedDictionary<TimelineEvent[]> = {};
    issues.forEach((issue, index) => {
        timelinesByIssueId[issue.id] = timelineForIssue(appId, issue, seedsWithSources[index].seed);
    });

    return { issues, timelinesByIssueId };
};

// Kept local so this fixture module stays free of component imports; same set as
// `CLOSED_ISSUE_STATES` in components/triage/triageHelpers and reducers/issues.
const CLOSED_STATES: IssueState[] = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

// Mirrors the backend list semantics (lib/runway/triage_helpers.go: GetIssuesWithPagination + SortIssues) so the
// demo list behaves like the real one: filters and counts apply before the open/closed split, then sort, then page.
const ISSUE_STATE_SORT_ORDER: IssueState[] = [
    IssueState.New,
    IssueState.Assigned,
    IssueState.Reopened,
    IssueState.FixRequested,
    IssueState.FixApproved,
    IssueState.FixMerged,
    IssueState.NotAPriority,
    IssueState.FixRejected,
];

const statePriority = (state: IssueState) => {
    const index = ISSUE_STATE_SORT_ORDER.indexOf(state);
    return index >= 0 ? index : 99;
};

const byCreatedAtDesc = (a: IssueEntity, b: IssueEntity) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();

const compareStrings = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase());

const emptyLast = (a: string, b: string) =>
    a === '' ? 1
    : b === '' ? -1
    : 0;

const sortDemoIssues = (issues: IssueEntity[], sortBy: TriageSortBy | undefined): IssueEntity[] => {
    const sorted = [...issues];
    switch (sortBy) {
        case 'date':
            return sorted.sort(byCreatedAtDesc);
        case 'source':
            return sorted.sort(
                (a, b) => compareStrings(a.sources[0]?.type ?? '', b.sources[0]?.type ?? '') || byCreatedAtDesc(a, b),
            );
        case 'owner':
            return sorted.sort((a, b) => {
                const ownerA = a.assigneeUserIds[0] ?? '';
                const ownerB = b.assigneeUserIds[0] ?? '';
                return emptyLast(ownerA, ownerB) || compareStrings(ownerA, ownerB) || byCreatedAtDesc(a, b);
            });
        // Every demo issue is applicable to all of its app's destinations, so this collapses to the date tiebreak.
        case 'destinations':
            return sorted.sort(byCreatedAtDesc);
        case 'triage':
        default:
            return sorted.sort((a, b) => statePriority(a.state) - statePriority(b.state) || byCreatedAtDesc(a, b));
    }
};

export const getDemoIssuesResponse = (
    allIssues: IssueEntity[],
    request: IssuesRequest,
    limit: number,
    offset: number,
): IssuesResponse => {
    let base = allIssues;

    if (request.sources?.length) {
        base = base.filter((issue) => issue.sources.some((source) => request.sources!.includes(source.type)));
    }
    if (request.versions?.length) {
        base = base.filter((issue) => issue.versions.some((version) => request.versions!.includes(version)));
    }
    if (request.owners?.length) {
        base = base.filter((issue) =>
            [...issue.assigneeUserIds, ...issue.assigneeGroupIds].some((owner) => request.owners!.includes(owner)),
        );
    }
    if (request.reporters?.length) {
        base = base.filter((issue) => !!issue.reporter && request.reporters!.includes(issue.reporter));
    }
    if (request.statuses?.length) {
        base = base.filter((issue) => request.statuses!.includes(issue.state));
    }
    if (request.search) {
        const search = request.search.toLowerCase();
        base = base.filter(
            (issue) => issue.title?.toLowerCase().includes(search) || issue.description?.toLowerCase().includes(search),
        );
    }

    const closedCount = base.filter((issue) => CLOSED_STATES.includes(issue.state)).length;
    const openCount = base.length - closedCount;

    const isClosedTab = request.statusGroup === 'closed';
    const page = sortDemoIssues(
        base.filter((issue) => CLOSED_STATES.includes(issue.state) === isClosedTab),
        request.sortBy,
    ).slice(offset, offset + limit);

    return { issues: page, openCount, closedCount, defaultStateSortOrder: ISSUE_STATE_SORT_ORDER };
};

/** Builds offered by the drawer's build selector in demo mode, in place of the live `GET /builds` call. */
export const getDemoBuildsForVersion = (issues: IssueEntity[], version: string): IssueBuild[] => {
    const byIdentifier = new Map<string, IssueBuild>();
    issues.forEach((issue) => {
        if (issue.build && issue.versions.includes(version)) {
            byIdentifier.set(issue.build.identifier, issue.build);
        }
    });
    return Array.from(byIdentifier.values());
};
